import { LogOut, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { DEFAULT_NEW_APP_ROUTE } from '@/modules/common/application/route-fallback';

import { useProjects } from '../hooks/use-projects';

interface ProjectGeneralTabProps {
  projectId: string;
  isOwner: boolean;
  onClose: () => void;
}

export const ProjectGeneralTab = ({ projectId, isOwner, onClose }: ProjectGeneralTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {
    updateProject,
    deleteProject,
    leaveProject,
    updateLoading,
    deleteLoading,
    leaveProjectLoading,
    removeProjectMemberLoading,
    getProject,
  } = useProjects();

  const project = getProject(projectId);

  useEffect(() => {
    if (project) {
      setTitle(project.title);
      setDescription(project.description || undefined);
    }
  }, [project]);

  const handleSave = async () => {
    try {
      await updateProject({
        variables: {
          input: {
            id: projectId,
            title: title?.trim() || undefined,
            description: description?.trim() || undefined,
          },
        },
      });
    } catch {
      // Error handling is done in the hook
    }
  };

  const handleLeave = async () => {
    try {
      await leaveProject({ projectId });
      setShowLeaveDialog(false);
      onClose();
      if (location.pathname.includes(`/project/${projectId}`)) {
        navigate(DEFAULT_NEW_APP_ROUTE);
      }
    } catch {
      // Error handling is done in the hook
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProject({
        variables: {
          id: projectId,
        },
      });
      setShowDeleteDialog(false);
      onClose();
      if (location.pathname.includes(`/project/${projectId}`)) {
        navigate(DEFAULT_NEW_APP_ROUTE);
      }
    } catch {
      // Error handling is done in the hook
    }
  };

  const hasChanges =
    title?.trim() !== project?.title || description?.trim() !== project?.description;

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-auto px-6 py-2 space-y-8">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium mb-4">
              {t('modules.mavue2.project.settings.details')}
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="project-name">
                  {t('modules.mavue2.project.create.projectName')}
                </Label>
                <Input
                  id="project-name"
                  value={title || ''}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={!isOwner}
                  placeholder={t('modules.mavue2.project.create.projectNamePlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-description">
                  {t('modules.mavue2.common.description')}
                </Label>
                <Textarea
                  id="project-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!isOwner}
                  placeholder={t('modules.mavue2.project.create.descriptionPlaceholder')}
                  rows={3}
                />
              </div>
              {isOwner && (
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges || updateLoading}
                  className="w-fit"
                >
                  {updateLoading && <Spinner className="mr-2" />}
                  {t('modules.mavue2.project.settings.saveChanges')}
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="border-t pt-6">
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-red-600">
              {t('modules.mavue2.project.settings.dangerZone')}
            </h3>
            <div className="space-y-2">
              {/* Leave Project */}
              <div className="flex items-center justify-between p-3 border border-amber-200 rounded-lg bg-amber-50">
                <div>
                  <h4 className="text-sm font-medium text-amber-900">
                    {t('modules.mavue2.project.settings.leaveProject')}
                  </h4>
                  <p className="text-xs text-amber-700">
                    {isOwner
                      ? t('modules.mavue2.project.settings.leaveOwnerDescription')
                      : t('modules.mavue2.project.settings.leaveDescription')}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowLeaveDialog(true)}
                  disabled={leaveProjectLoading || removeProjectMemberLoading}
                  className="border-amber-300 text-amber-700 hover:bg-amber-100 h-8"
                >
                  <LogOut className="mr-1 h-3 w-3" />
                  {t('modules.mavue2.project.settings.leave')}
                </Button>
              </div>

              {/* Delete Project - Only for owners */}
              {isOwner && (
                <div className="flex items-center justify-between p-3 border border-red-200 rounded-lg bg-red-50">
                  <div>
                    <h4 className="text-sm font-medium text-red-900">
                      {t('modules.mavue2.navigation.deleteProject')}
                    </h4>
                    <p className="text-xs text-red-700">
                      {t('modules.mavue2.project.settings.deleteDescription')}
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={deleteLoading}
                    className="h-8"
                  >
                    <Trash2 className="mr-1 h-3 w-3" />
                    {t('modules.mavue2.common.delete')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Leave Project Confirmation Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('modules.mavue2.project.settings.leaveProject')}</DialogTitle>
            <DialogDescription>
              {isOwner
                ? t('modules.mavue2.project.settings.leaveConfirmOwner')
                : t('modules.mavue2.project.settings.leaveConfirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveDialog(false)}>
              {t('modules.mavue2.common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleLeave}
              disabled={leaveProjectLoading || removeProjectMemberLoading}
            >
              {leaveProjectLoading || (removeProjectMemberLoading && <Spinner className="mr-2" />)}
              {t('modules.mavue2.project.settings.leaveProject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Project Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('modules.mavue2.navigation.deleteProject')}</DialogTitle>
            <DialogDescription>
              {t('modules.mavue2.project.settings.deleteConfirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {t('modules.mavue2.common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteLoading}>
              {deleteLoading && <Spinner className="mr-2" />}
              {t('modules.mavue2.navigation.deleteProject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
